import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { kennoTenantReducer } from './kennoTenantReducer';
import { Dispatcher, loadUser } from './kennoTenantActions';
import { LOAD_STATES } from './common';
import { BillingMonth } from '../types/tampuuri';
import { PublicEvent } from '../types/events';
import { PublicNotice } from '../types/notices';
import { ContractTerminationStatus, TerminationNotice } from '../pages/y-saatio/termination/schema';
import { KennoNotification } from '../types/notifications';
import { Form } from '../types/forms';

interface KennoProfileAttributes {
  uuid: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  cognitoId?: string;
}

export interface KennoUserState {
  isLoggedIn: boolean;
  isLoaded: boolean;
  attributes?: KennoProfileAttributes;
}

export interface BillingHistoryState {
  loadState?: LOAD_STATES | false;
  items?: BillingMonth[];
  filter: 'all' | 'billed' | 'paid';
  rangeStart?: number;
  rangeEnd?: number;
}

interface BillingItem {
  targetId: number;
  nameOnBill: string;
  dueDate: string;
  expected: string;
}

export interface CurrentBillState {
  loadState?: LOAD_STATES | false;
  totalRent?: number;
  difference?: number;
  debt?: number;
  dueDate?: Date | null;
  items?: BillingItem[];
}

interface CurrentTargets {
  monthlyRent?: number | null;
  monthlyWaterAdvance?: number | null;
}

export interface CurrentTargetsState {
  loadState?: LOAD_STATES | false;
  data?: CurrentTargets;
}

interface DebtByTarget {
  currentDue: string;
  bankAccount: string;
  dueDate: string;
  nameOnBill: string;
  targetId: number;
}

interface DebtByMonth {
  currentDue: string;
  debtByTarget: DebtByTarget[];
  yearMonth: string;
}

interface Debt {
  currentDue: string;
  debtByMonth: DebtByMonth[];
  referenceNumber: string;
}

export interface DebtState {
  loadState: LOAD_STATES | false;
  data?: Debt;
}

interface SecurityDepositVoucher {
  id: number;
  amount: number;
  remaining: number;
  usedInVoucherWithholdings: number;
  redeemedOrRelinquished: boolean;
}

interface SecurityDeposit {
  targetId: number;
  amount: number;
  totalAmount: number;
  remainingUnpaid: number;
  dueDate: Date;
  usedInPaymentOrders: number;
  usedInWithholdings: number;
  totalUsed: number;
  remaining: number;
  voucher: SecurityDepositVoucher | null;
}

export interface SecurityDepositState {
  loadState: LOAD_STATES | false;
  data?: SecurityDeposit;
}

export interface Address {
  id?: number;
  countryName?: string;
  countryCode?: string;
  streetAddressLine1: string;
  streetAddressLine2: string | null;
  city: string;
  postalCode: string;
}

export interface ContractTenant {
  firstName: string;
  lastName: string;
}

interface TerminationRequest {
  status: ContractTerminationStatus | null;
  createdAt: string | null;
}

interface Collateral {
  bankName: string | null;
  isCollateral: boolean;
}

export enum KennoTerminationDisallowedReason {
  CONTRACT_TERMINATED = 'contract_terminated',
  PENDING_TERMINATION_TENANT_APPROVALS = 'pending_termination_tenant_approvals',
  PENDING_TERMINATION_REVIEW = 'pending_termination_review',
  IS_ESTATE_CONTRACT = 'is_estate_contract',
  HAS_GUARDIAN_OF_INTERESTS = 'has_guardian_of_interests',
  MISSING_RESIDENT_USERS = 'missing_resident_users',
  CONTRACT_TAG = 'contract_tag',
  NOT_CONTRACT_PARTY = 'not_contract_party',
  TENANTS_WITH_UNDETERMINED_AGE = 'tenants_with_undetermined_age',
  HAS_COMPANY_TENANT = 'has_company_tenant',
  PENDING_MODIFICATION_REQUEST = 'pending_modification_request',
}

export interface Contract {
  uuid: string;
  startDate: Date;
  endDate: Date | null;
  realPropertyName: string;
  postalAddress: Address;
  status: string; // TODO: consider using an enum
  addressDetails: {
    staircaseLetter: string;
    apartmentNumber: string;
  };
  contractType: string; // TODO: consider using an enum
  propertyCompanyName: string;
  referenceNumber: string;
  propertyCompanyAddress: Address;
  tenant: {
    isPayer: boolean;
    isContractParty: boolean;
    tenantRole: string; // TODO: consider using an enum
    id: number;
  };
  language: string;
  terminationDisallowedReason?: KennoTerminationDisallowedReason;
  contractTenants?: {
    contractParties: ContractTenant[];
    otherParties: ContractTenant[];
  };
  terminationRequest?: TerminationRequest;
  collateral?: Collateral;
}

export interface ContractState {
  loadState: LOAD_STATES | false;
  data?: Contract;
}

export interface ApartmentAttributes {
  rentableApartmentId: number;
  rentalApartmentId: number | null;
  rightOfOccupancyApartmentId: number | null;
  postalAddress: Address | null;
  floor: number | null;
  technicalTypeLabel: string | null;
  area: string | null;
  salesDescription: string | null;
  rentalApartmentStatus: RentalApartmentStatus | null;
  rightOfOccupancyApartmentStatus: RightOfOccupancyApartmentStatus | null;
  contractStatus: ContractStatus | null;
  visibleOnHomePage: boolean;
  images: Array<string>;
}

export interface ApartmentState {
  loadState?: LOAD_STATES | false;
  data?: ApartmentAttributes;
}

type RentalApartmentStatus =
  | 'active_contract'
  | 'active_offer'
  | 'attachments_received'
  | 'becoming_vacant'
  | 'contract_signature_pending'
  | 'interested_in_viewing'
  | 'offer_accepted'
  | 'rejected'
  | 'reserved'
  | 'vacant';

type RightOfOccupancyApartmentStatus =
  | 'active_contract'
  | 'becoming_vacant'
  | 'contract_created'
  | 'offer_round_finished'
  | 'offer_round_open'
  | 'reserved'
  | 'vacant';

type ContractStatus =
  | 'active'
  | 'cancelled'
  | 'draft'
  | 'expired'
  | 'signature_cancelled'
  | 'signature_expired'
  | 'signed'
  | 'terminated'
  | 'waiting_for_signature';

export interface Building {
  maintenanceCompany: {
    name: string | null;
    emergencyNumber: string | null;
  };
  emergencyPlan: {
    url: string | null;
  };
}

export interface BuildingState {
  loadState: LOAD_STATES | false;
  data?: Building;
}

export interface TerminationNoticeState {
  loadState: LOAD_STATES | false;
  data?: TerminationNotice;
}
interface Link {
  href: string;
  name: string;
}

export type Folder1 = 'tiedote' | 'Kodin kansio';

export type Folder2 =
  | 'Kodinhoito ja -huolto'
  | 'Kodinkoneet'
  | 'Lämmitys ja ilmanvaihto'
  | 'Tietoa asumisesta'
  | 'Turvallisuus'
  | 'TV- ja tietoliikenne';

export enum MfilesFormType {
  DOCUMENT = 'document',
}

export interface MFilesLink extends Link {
  href: string;
  name: string;
  origName: string;
  creationDate: string;
  id?: string;
  formType: MfilesFormType;
  publishedAt?: Date;
  Folder1: Folder1;
  Folder2: Folder2;
}

export interface Documents {
  costCenterLinks: MFilesLink[];
  buildingLinks: MFilesLink[];
  apartmentLinks: MFilesLink[];
}

export interface DocumentsState {
  loadState: LOAD_STATES | false;
  data?: Documents;
}

export interface NotificationsState {
  loadState: LOAD_STATES | false;
  data?: KennoNotification[];
}

export interface KennoTenantState {
  user: KennoUserState;
  billingHistory: BillingHistoryState;
  currentBill: CurrentBillState;
  currentTargets: CurrentTargetsState;
  contract: ContractState;
  apartment: ApartmentState;
  building: BuildingState;
  documents: DocumentsState;
  debt: DebtState;
  securityDeposit: SecurityDepositState;
  publicNotices: PublicNoticesState;
  publicEvents: PublicEventsState;
  terminationNotice: TerminationNoticeState;
  notifications: NotificationsState;
  ownForms: OwnFormsState;
}

export interface PublicNoticesState {
  loadState: LOAD_STATES | false;
  data?: PublicNotice[];
}

export interface PublicEventsState {
  loadState: LOAD_STATES | false;
  data?: PublicEvent[];
}

export interface OwnFormsState {
  loadState: LOAD_STATES | false;
  data?: Form[];
}

export const initialState: KennoTenantState = {
  user: {
    isLoggedIn: false,
    isLoaded: false,
  },
  billingHistory: { filter: 'all', loadState: false },
  currentBill: { loadState: false },
  currentTargets: { loadState: false },
  contract: { loadState: false },
  apartment: { loadState: false },
  building: { loadState: false },
  documents: { loadState: false },
  debt: { loadState: false },
  securityDeposit: { loadState: false },
  publicNotices: { loadState: false },
  publicEvents: { loadState: false },
  terminationNotice: { loadState: false },
  notifications: { loadState: false },
  ownForms: { loadState: false },
};

const KennoTenantStateContext = createContext({} as KennoTenantState);
const KennoTenantDispatchContext = createContext((() => {}) as Dispatcher);

function KennoTenantStateProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(kennoTenantReducer, initialState);
  useEffect(() => {
    loadUser(dispatch);
  }, [dispatch]);

  return (
    <KennoTenantStateContext.Provider value={state}>
      <KennoTenantDispatchContext.Provider value={dispatch}>{children}</KennoTenantDispatchContext.Provider>
    </KennoTenantStateContext.Provider>
  );
}

function useKennoTenantState() {
  const context = useContext(KennoTenantStateContext);
  if (context === undefined) {
    throw new Error('useKennoTenantState must be used within a KennoTenantStateProvider');
  }
  return context;
}

function useKennoTenantDispatch() {
  const context = useContext(KennoTenantDispatchContext);
  if (context === undefined) {
    throw new Error('useKennoTenantDispatch must be used within a KennoTenantStateProvider');
  }
  return context;
}

function useKennoTenant(): [KennoTenantState, Dispatcher] {
  return [useKennoTenantState(), useKennoTenantDispatch()];
}

export { KennoTenantStateProvider, useKennoTenantState, useKennoTenantDispatch, useKennoTenant };
